import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import AppointmentActions from 'stores/Appointments/Actions'
import {
  getCheckinCache,
  getApptsOfCheckinLoading
} from '@web/stores/Appointments/Select'

const AVAILABLE_DAYS_FOR_CHECKIN = 5

const filterUpcomingWithinWindow = (upcomingAppointments, daysWindow) => {
  if (!Array.isArray(upcomingAppointments)) {
    return []
  }
  const today = new Date()
  return upcomingAppointments.filter(appointment => {
    const appointmentDate = new Date(appointment?.datetime)
    if (Number.isNaN(appointmentDate.getTime())) {
      return false
    }
    const timeDifference = appointmentDate.getTime() - today.getTime()
    const daysDifference = timeDifference / (1000 * 3600 * 24)
    return daysDifference <= daysWindow
  })
}

export const useCheckinTrigger = ({
  patientId,
  upcomingAppointments,
  daysWindow = AVAILABLE_DAYS_FOR_CHECKIN,
  enabled = true,
  isFocused = true
}) => {
  const dispatch = useDispatch()
  const checkinCache = useSelector(getCheckinCache)
  const currentLoadingApptsId = useSelector(getApptsOfCheckinLoading)
  const lastTriggeredRef = useRef('')

  useEffect(() => {
    if (!enabled || !patientId) {
      return
    }
    if (!Array.isArray(upcomingAppointments)) {
      return
    }
    const matchingAppointments = filterUpcomingWithinWindow(
      upcomingAppointments,
      daysWindow
    )
    if (matchingAppointments.length === 0) {
      return
    }
    const arrAppointmentId = matchingAppointments
      .map(appointment => appointment.appointmentId)
      .filter(Boolean)

    if (arrAppointmentId.length === 0) {
      return
    }

    const signature = `${patientId}|${[...arrAppointmentId].sort().join(',')}`
    if (lastTriggeredRef.current === signature) {
      return
    }
    lastTriggeredRef.current = signature

    dispatch(AppointmentActions.getCheckin(arrAppointmentId, patientId))
  }, [
    enabled,
    patientId,
    upcomingAppointments,
    daysWindow,
    checkinCache,
    currentLoadingApptsId,
    dispatch
  ])

  // Reset the per-appointment loading spinner when the screen regains focus.
  // Without this, the spinner set by List/Item before opening the check-in
  // WebView would never clear on return (the signature guard above blocks
  // the saga from re-running, so GET_CHECKIN_SUCCESS never fires to reset it).
  const wasFocusedRef = useRef(isFocused)
  useEffect(() => {
    const gainedFocus = isFocused && !wasFocusedRef.current
    wasFocusedRef.current = isFocused
    if (!gainedFocus) {
      return
    }
    if (!enabled || !patientId) {
      return
    }
    if (!currentLoadingApptsId) {
      return
    }
    dispatch(AppointmentActions.setApptsOfCheckinLoading(null))
  }, [isFocused, enabled, patientId, currentLoadingApptsId, dispatch])
}

export default useCheckinTrigger
